/**
 * Configuration des conditions de reussite.
 *
 * Chaque condition est desormais typee a la saisie et son interpretation est
 * montree a l'enseignant : c'est ce meme libelle que l'eleve lira en cas
 * d'echec, ce qui evite d'ecrire une condition dont le message resultant serait
 * incomprehensible.
 */

import React, { useState } from 'react';
import { Button, Card, Empty, Input, List, Space, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { HtmlCssConfig } from '../../models/htmlcss/types';
import { evaluateWinConditions } from './core/winConditions';
import { ConditionKind } from './core/types';

interface HtmlCssWinConditionPanelProps {
  initialConfig: HtmlCssConfig;
  onSave?: (config: HtmlCssConfig) => void;
}

const KIND_LABELS: Record<ConditionKind, string> = {
  'html-element': 'Balise HTML',
  'css-property': 'Propriété CSS',
  'css-selector': 'Sélecteur CSS',
  text: 'Texte libre',
};

const KIND_COLORS: Record<ConditionKind, string> = {
  'html-element': 'blue',
  'css-property': 'green',
  'css-selector': 'purple',
  text: 'default',
};

/** Determine la nature d'une condition en reutilisant le moteur d'evaluation. */
const describeCondition = (condition: string): ConditionKind => {
  // On evalue contre un document vide : seule la classification importe ici.
  const evaluation = evaluateWinConditions('', '', [condition]);
  return evaluation.results[0]?.kind ?? 'text';
};

const HtmlCssWinConditionPanel: React.FC<HtmlCssWinConditionPanelProps> = ({
  initialConfig,
  onSave,
}) => {
  const [conditions, setConditions] = useState<string[]>(
    initialConfig.config.winConditions ?? []
  );
  const [draft, setDraft] = useState('');

  const addCondition = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      message.error('Saisissez une condition.');
      return;
    }
    if (conditions.includes(trimmed)) {
      message.warning('Cette condition existe déjà.');
      return;
    }
    setConditions(current => [...current, trimmed]);
    setDraft('');
  };

  const updateCondition = (index: number, value: string) => {
    setConditions(current => current.map((item, i) => (i === index ? value : item)));
  };

  const removeCondition = (index: number) => {
    setConditions(current => current.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave?.({
      ...initialConfig,
      config: {
        ...initialConfig.config,
        // Les lignes vides sont ecartees : elles ne doivent pas faire echouer
        // l'exercice ni apparaitre dans le message affiche a l'eleve.
        winConditions: conditions.map(c => c.trim()).filter(c => c !== ''),
      },
    });
    message.success('Conditions enregistrées.');
  };

  return (
    // La carte se structure en trois zones : l'introduction et la barre
    // d'actions restent visibles, seule la liste defile. Sans cela une longue
    // liste de conditions poussait le bouton d'enregistrement hors de l'ecran.
    <Card
      title="Conditions de réussite"
      variant="borderless"
      className="htmlcss-config-panel"
      styles={{ body: { display: 'flex', flexDirection: 'column', minHeight: 0 } }}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
        Formats acceptés : <code>&lt;h1&gt;</code> pour une balise,{' '}
        <code>color: blue</code> pour une propriété, <code>.ma-classe {'{ }'}</code> pour un
        sélecteur, ou du texte libre. Sans aucune condition, l'exercice est validé dès son
        exécution.
      </Typography.Paragraph>

      <div className="htmlcss-config-scroll">
        {conditions.length === 0 ? (
          <Empty description="Aucune condition : l'exercice sera validé dès son exécution." />
        ) : (
          <List
            dataSource={conditions}
          renderItem={(item, index) => {
            const kind = describeCondition(item);
            return (
              <List.Item
                actions={[
                  <Button
                    key="delete"
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    onClick={() => removeCondition(index)}
                    aria-label={`Supprimer la condition ${item}`}
                  />,
                ]}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  <Input
                    value={item}
                    onChange={event => updateCondition(index, event.target.value)}
                  />
                  <Tag color={KIND_COLORS[kind]}>{KIND_LABELS[kind]}</Tag>
                </Space>
              </List.Item>
              );
            }}
          />
        )}
      </div>

      {/* Barre fixe : toujours atteignable, quelle que soit la longueur de la
          liste au-dessus. */}
      <div className="htmlcss-config-footer">
        <Input
          placeholder="Nouvelle condition"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onPressEnter={addCondition}
        />
        <Button icon={<PlusOutlined />} onClick={addCondition}>
          Ajouter
        </Button>
        <Button type="primary" onClick={handleSave}>
          Enregistrer
        </Button>
      </div>
    </Card>
  );
};

export default HtmlCssWinConditionPanel;
